import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Helpers } from '../models/helpers';
import { SCREEN_KIOSK_SESSION } from '../models/screen-kiosk.config';
import { AuthService } from '../services/auth.service';
import {
  CurrentUser,
  SessionService,
} from '../services/shared/session.service';

/**
 * ScreenTokenGuard — auto-login del monitor magazzino su /warehouseorders.
 *
 * Il monitor (account Screen) gira 24/7 non presidiato: dopo un update
 * dell'app o un blackout la sessione in localStorage può sparire e
 * qualcuno dovrebbe andare fisicamente a rifare login. Per evitarlo,
 * l'URL del kiosk trasporta il token:
 *
 *   /warehouseorders?token=<token>            (URL-encoded: contiene + / =)
 *   /warehouseorders?email=<e>&account_id=<id>&token=<token>
 *
 * Due direzioni complementari:
 *
 * 1. Token nell'URL → sessione. Se `token` è nei query params e la
 *    sessione corrente non coincide, viene ricostruita da zero
 *    (email/account_id opzionali: mancando valgono i default hardcoded
 *    di `SCREEN_KIOSK_SESSION`). Se coincide già non si tocca nulla,
 *    per non sovrascrivere ad ogni navigazione l'oggetto utente
 *    completo restituito dal login vero.
 * 2. Sessione → token nell'URL. Se un account Screen naviga qui SENZA
 *    token nell'URL (es. login fatto a mano dal form), si redirige
 *    sulla stessa destinazione aggiungendo `?token=...`: così l'URL
 *    del monitor contiene SEMPRE il token e il session-restore del
 *    browser dopo un blackout ri-logga da solo.
 *
 * Senza token e senza sessione Screen il guard non fa nulla: un utente
 * non loggato viene rimandato a /login dai guard successivi, come nel
 * resto dell'app. (Il token nella barra URL non è un segreto in più:
 * è comunque estraibile dal bundle JS.)
 */
@Injectable({ providedIn: 'root' })
export class ScreenTokenGuard {

  constructor(
    private router: Router,
    private session: SessionService,
    private authService: AuthService,
  ) {}

  canMatch(): boolean | UrlTree {
    const target = this.router.getCurrentNavigation()?.extractedUrl;
    const queryParams = target?.queryParams ?? {};
    const currentUser = this.session.getCurrentUser();

    const token = this.readParam(queryParams, 'token');
    if (token) {
      if (currentUser?.ac_token !== token) {
        this.authService.applyKioskSession(this.buildOverrides(token, queryParams));
      }
      return true;
    }

    const isScreen =
      currentUser?.ac_user_type_cod === Helpers.PermisionScreen;
    if (target && isScreen) {
      target.queryParams = {
        ...queryParams,
        token: currentUser.ac_token || SCREEN_KIOSK_SESSION.token,
      };
      return target;
    }

    return true;
  }

  private buildOverrides(
    token: string,
    queryParams: Record<string, unknown>,
  ): Partial<CurrentUser> {
    const overrides: Partial<CurrentUser> = { ac_token: token };
    const email = this.readParam(queryParams, 'email');
    if (email) {
      overrides.ac_email = email;
    }
    // account_id arriva come stringa dall'URL; se assente o non numerico
    // resta il default della config hardcoded.
    const accountId = Number(this.readParam(queryParams, 'account_id'));
    if (Number.isFinite(accountId) && accountId > 0) {
      overrides.account_id = accountId;
    }
    return overrides;
  }

  private readParam(
    queryParams: Record<string, unknown>,
    key: string,
  ): string {
    const value = queryParams[key];
    return typeof value === 'string' ? value.trim() : '';
  }

}
